import type { CSSProperties, ReactElement, ReactNode } from 'react'
import { WalkIcon } from './WalkIcon'
import { StarIcon } from './StarIcon'
import { FirstCarGlyph, MiddleCarGlyph } from './CarGlyphs'
import { FirstStopMarker, LastStopMarker, MiddleStopMarker, LineConnector } from './LinePrimitives'
import { RouteDetailsStrings, type RouteDetailsStringSet } from './strings'
import { highlightedCarIndices, transferCaption, type ConvenientCarType } from './carTypes'
import { RouteDetailsTestTags } from './testTags'
import trainUrl from './assets/train.png'
import type { FirstStopExtra } from './sections'
import styles from './rows.module.css'

/**
 * The row components take their localised copy as a `strings` prop,
 * defaulting to the English {@link RouteDetailsStrings} constant — the
 * same domain-agnostic, i18n-free injection seam the sibling V2 packages
 * (RouteCard / StationCard / StationDetails) use for `copy`. The
 * host (`MobileRouteDetailsOverlay`) builds the localised set from the
 * i18next catalog and threads it in; a provider-less render (unit test /
 * standalone) falls back to the English default, byte-identical to the
 * previous `useTranslation` fallback.
 */

/**
 * RouteDetails row components — stateless pixel ports of the iOS
 * `Views/Rows/*` and `Views/ExtraViews/*` views. Each takes a plain
 * props bag (no store, no context) and renders the same element set /
 * copy / a11y id as iOS + Android (QA spec §3, §6). Line colour flows
 * through `--rd-line-color`; the rail column is a fixed 28px-wide
 * `position: relative` box so the connector bar runs continuously behind
 * the markers (see LinePrimitives).
 */

function colorVar(color: string): CSSProperties {
  return { '--rd-line-color': color } as CSSProperties
}

// ─── Validation (tap in / tap out) ──────────────────────────────────

export function ValidationRow({
  type,
  strings = RouteDetailsStrings,
}: {
  type: 'entry' | 'exit'
  strings?: RouteDetailsStringSet
}): ReactElement {
  return (
    <div
      className={styles.validation}
      data-testid={
        type === 'exit' ? RouteDetailsTestTags.validationExit : RouteDetailsTestTags.validationEntry
      }
    >
      {type === 'exit' ? strings.tapOut : strings.tapIn}
    </div>
  )
}

// ─── Entry / Exit (walk to platform / exit) ─────────────────────────

export function EntryRow({
  type,
  totalTime,
  strings = RouteDetailsStrings,
}: {
  type: 'entry' | 'exit'
  totalTime: string
  strings?: RouteDetailsStringSet
}): ReactElement {
  return (
    <div
      className={styles.entry}
      data-testid={type === 'exit' ? RouteDetailsTestTags.exit : RouteDetailsTestTags.entry}
    >
      <span className={styles.entryIcon}>
        <WalkIcon size={30} />
      </span>
      <span className={styles.entryLabel}>
        {type === 'exit' ? strings.walkToTheExit : strings.walkToThePlatform}
      </span>
      <span className={styles.entryTime}>{totalTime}</span>
    </div>
  )
}

// ─── Rail wrapper ───────────────────────────────────────────────────

/**
 * Wraps a row's left gutter (arrival-time column + rail) and right
 * content column. `arrival` is the 36px right-aligned time cell;
 * `rail` is the 28px marker/connector column; `children` the content.
 */
function RailRow({
  arrival,
  rail,
  children,
  align = 'top',
  testId,
  className,
}: {
  arrival: ReactNode
  rail: ReactNode
  children: ReactNode
  align?: 'top' | 'center' | 'bottom'
  testId?: string
  className?: string
}): ReactElement {
  const alignClass =
    align === 'bottom' ? styles.rowBottom : align === 'center' ? styles.rowCenter : styles.rowTop
  return (
    <div
      className={[styles.railRow, alignClass, className].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      <span className={styles.arrivalCell}>{arrival}</span>
      <span className={styles.rail}>{rail}</span>
      <div className={styles.content}>{children}</div>
    </div>
  )
}

// ─── FirstStop ──────────────────────────────────────────────────────

export interface FirstStopRowProps {
  sectionIndex: number
  title: string
  subtitle: string | null
  totalTime: string
  arrivalTime: string
  hexColor: string
  isOutlined: boolean
  isSingleStop: boolean
  /** Platform sign ("Westbound", …), verbatim; null/undefined → no chip. */
  platformBound?: string | null
  /** "Towards <next stop>"; null/undefined on a single-stop block. */
  towards?: string | null
  extra: FirstStopExtra
  onUpsellTap: () => void
  strings?: RouteDetailsStringSet
}

export function FirstStopRow(props: FirstStopRowProps): ReactElement {
  const {
    sectionIndex,
    title,
    subtitle,
    totalTime,
    arrivalTime,
    hexColor,
    isOutlined,
    isSingleStop,
    platformBound = null,
    towards = null,
    extra,
    onUpsellTap,
    strings = RouteDetailsStrings,
  } = props
  return (
    <RailRow
      testId={RouteDetailsTestTags.firstStop(sectionIndex)}
      arrival={<span className={styles.timeText}>{arrivalTime}</span>}
      rail={
        <span className={styles.railStack} style={colorVar(hexColor)}>
          {!isSingleStop && <LineConnector color={hexColor} outlined={isOutlined} />}
          <span className={styles.railTop}>
            <FirstStopMarker color={hexColor} />
          </span>
        </span>
      }
    >
      <div className={styles.firstStopHead}>
        <div className={styles.firstStopTitles}>
          <span className={styles.stopTitle}>{title}</span>
          {subtitle !== null && (
            <span className={styles.lineSubtitle} style={{ color: hexColor }}>
              {subtitle}
            </span>
          )}
        </div>
        {!isSingleStop && totalTime !== '' && <span className={styles.totalTime}>{totalTime}</span>}
      </div>
      {platformBound !== null && <span className={styles.platformBoundChip}>{platformBound}</span>}
      {towards !== null && (
        // Keeps the former direction row's test id (cross-platform tag contract).
        <span
          className={styles.towardsLabel}
          data-testid={RouteDetailsTestTags.direction(sectionIndex)}
        >
          {towards}
        </span>
      )}
      {extra?.kind === 'possibleTransfer' && (
        <PossibleTransferStrip sectionIndex={sectionIndex} cars={extra.cars} strings={strings} />
      )}
      {extra?.kind === 'upsell' && <UpsellBanner onTap={onUpsellTap} strings={strings} />}
    </RailRow>
  )
}

// ─── Expand ─────────────────────────────────────────────────────────

export function ExpandRow({
  sectionIndex,
  title,
  isExpanded,
  hexColor,
  isOutlined,
  onToggle,
}: {
  sectionIndex: number
  title: string
  isExpanded: boolean
  hexColor: string
  isOutlined: boolean
  onToggle: () => void
}): ReactElement {
  return (
    <RailRow
      align="center"
      testId={RouteDetailsTestTags.expandRow(sectionIndex)}
      arrival={null}
      rail={
        <span className={styles.railStack} style={colorVar(hexColor)}>
          <LineConnector color={hexColor} outlined={isOutlined} />
        </span>
      }
    >
      <button
        type="button"
        className={styles.expandButton}
        data-testid={RouteDetailsTestTags.expandButton(sectionIndex)}
        aria-expanded={isExpanded}
        onClick={onToggle}
      >
        <span className={styles.expandLabel}>{title}</span>
        <Chevron up={isExpanded} />
      </button>
    </RailRow>
  )
}

function Chevron({ up }: { up: boolean }): ReactElement {
  return (
    <svg
      className={styles.chevron}
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
      data-chevron={up ? 'up' : 'down'}
    >
      <path
        d={up ? 'M3.5 10L8 5.5L12.5 10' : 'M3.5 6L8 10.5L12.5 6'}
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ─── MiddleStop ─────────────────────────────────────────────────────

export function MiddleStopRow({
  sectionIndex,
  rowIndex,
  title,
  arrivalTime,
  hexColor,
  isOutlined,
}: {
  sectionIndex: number
  rowIndex: number
  title: string
  arrivalTime: string
  hexColor: string
  isOutlined: boolean
}): ReactElement {
  return (
    <RailRow
      align="center"
      testId={RouteDetailsTestTags.middleStop(sectionIndex, rowIndex)}
      // iOS renders the time cell with opacity 0 (kept for width parity).
      arrival={
        <span className={`${styles.timeText} ${styles.timeHidden}`} aria-hidden="true">
          {arrivalTime}
        </span>
      }
      rail={
        <span className={styles.railStack} style={colorVar(hexColor)}>
          <LineConnector color={hexColor} outlined={isOutlined} />
          <span className={styles.railCenterNotch}>
            <MiddleStopMarker color={hexColor} outlined={isOutlined} />
          </span>
        </span>
      }
    >
      <span className={styles.middleLabel}>{title}</span>
    </RailRow>
  )
}

// ─── LastStop ───────────────────────────────────────────────────────

export function LastStopRow({
  sectionIndex,
  title,
  arrivalTime,
  hexColor,
  isOutlined,
}: {
  sectionIndex: number
  title: string
  arrivalTime: string
  hexColor: string
  isOutlined: boolean
}): ReactElement {
  return (
    <RailRow
      align="bottom"
      testId={RouteDetailsTestTags.lastStop(sectionIndex)}
      arrival={<span className={styles.timeText}>{arrivalTime}</span>}
      rail={
        <span className={styles.railStack} style={colorVar(hexColor)}>
          {/* The connecting line runs into the marker and STOPS at its centre
              (stopAtCenter) — mirrors iOS's per-row line + the marker overlap;
              without stopAtCenter the bar poked ~6px out below the ring. */}
          <LineConnector color={hexColor} outlined={isOutlined} stopAtCenter />
          <span className={styles.railBottom}>
            <LastStopMarker color={hexColor} />
          </span>
        </span>
      }
    >
      <span className={styles.stopTitle}>{title}</span>
    </RailRow>
  )
}

// ─── Transfer ───────────────────────────────────────────────────────

export function TransferRow({
  sectionIndex,
  title,
  totalTime,
}: {
  sectionIndex: number
  title: string
  totalTime: string
}): ReactElement {
  return (
    <div className={styles.transfer} data-testid={RouteDetailsTestTags.transfer(sectionIndex)}>
      <span className={styles.transferIcon}>
        <WalkIcon size={30} />
      </span>
      <span className={styles.transferLabel}>{title}</span>
      <span className={styles.transferTime}>{totalTime}</span>
    </div>
  )
}

// ─── Street walk (2026-07 street-walk contract) ─────────────────────

/**
 * Pedestrian leg between two different stations (same-name cluster
 * siblings, e.g. Edgware Road Circle → Bakerloo). Same visual shape as
 * TransferRow — walk figure + label + duration — but the label names
 * the walk target: "Walk to <station> (<line>)".
 */
export function StreetWalkRow({
  sectionIndex,
  stationName,
  lineLabel,
  totalTime,
  strings = RouteDetailsStrings,
}: {
  sectionIndex: number
  stationName: string
  lineLabel: string | null
  totalTime: string
  strings?: RouteDetailsStringSet
}): ReactElement {
  return (
    <div className={styles.transfer} data-testid={RouteDetailsTestTags.streetWalk(sectionIndex)}>
      <span className={styles.transferIcon}>
        <WalkIcon size={30} />
      </span>
      <span className={styles.transferLabel}>{strings.walkToStation(stationName, lineLabel)}</span>
      <span className={styles.transferTime}>{totalTime}</span>
    </div>
  )
}

// ─── Street endpoint marker (iOS canon @c9d7ecc61) ──────────────────

/**
 * Selected-station endpoint of a street walk: a NEUTRAL hollow ring
 * (secondary text colour stroke, same 28×28 open-ring geometry as
 * FirstStopMarker) + bold station name. No line chip, no time — the
 * station is named as a place on the street, not as a line's platform.
 */
export function StreetEndpointRow({
  sectionIndex,
  role,
  stationName,
  leadsList = false,
}: {
  sectionIndex: number
  role: 'origin' | 'destination'
  stationName: string
  /**
   * True when this row is the first thing in the itinerary, which
   * happens on a walking route: it has no tap-in / entry rows, so the
   * station title butted straight against the route-variant selector
   * above it with nothing between them. A plain route opens with the
   * small "tap in" text line, which reads as a gap; this one opens with
   * a bold title, which reads as a collision.
   */
  leadsList?: boolean
}): ReactElement {
  const testId =
    role === 'origin'
      ? RouteDetailsTestTags.streetEndpointOrigin(sectionIndex)
      : RouteDetailsTestTags.streetEndpointDestination(sectionIndex)
  return (
    <RailRow
      className={leadsList ? styles.leadsList : undefined}
      testId={testId}
      arrival={<span className={styles.timeText} />}
      rail={
        <span className={styles.railStack}>
          <span className={styles.railTop}>
            <FirstStopMarker color="var(--mm-color-text-secondary)" />
          </span>
        </span>
      }
    >
      <span className={styles.stopTitle}>{stationName}</span>
    </RailRow>
  )
}

// ─── PossibleTransfer car-strip ─────────────────────────────────────

const CAR_TOTAL = 5

export function PossibleTransferStrip({
  sectionIndex,
  cars,
  strings = RouteDetailsStrings,
}: {
  sectionIndex: number
  cars: readonly ConvenientCarType[]
  strings?: RouteDetailsStringSet
}): ReactElement {
  const highlighted = highlightedCarIndices(cars)
  return (
    <div
      className={styles.carStrip}
      data-testid={RouteDetailsTestTags.possibleTransfer(sectionIndex)}
    >
      <div className={styles.carRow}>
        {Array.from({ length: CAR_TOTAL }, (_, index) => {
          const isHighlighted = highlighted.has(index)
          return (
            <div
              key={index}
              className={styles.carCell}
              data-car-cell
              data-highlighted={isHighlighted ? 'true' : 'false'}
            >
              <span className={styles.carGlyph} data-highlighted={isHighlighted ? 'true' : 'false'}>
                {index === 0 ? <FirstCarGlyph /> : <MiddleCarGlyph />}
              </span>
              <span
                className={styles.carUnderline}
                data-highlighted={isHighlighted ? 'true' : 'false'}
              />
            </div>
          )
        })}
      </div>
      <p className={styles.carCaption}>{transferCaption(cars, strings)}</p>
    </div>
  )
}

// ─── UpsellBanner ───────────────────────────────────────────────────

export function UpsellBanner({
  onTap,
  strings = RouteDetailsStrings,
}: {
  onTap: () => void
  strings?: RouteDetailsStringSet
}): ReactElement {
  return (
    <button
      type="button"
      className={styles.upsell}
      data-testid={RouteDetailsTestTags.upsellBanner}
      onClick={onTap}
    >
      <span className={styles.upsellStar}>
        <StarIcon size={26} />
      </span>
      <span className={styles.upsellTitle}>{strings.upsellTitle}</span>
      <img className={styles.upsellTrain} src={trainUrl} alt="" aria-hidden="true" />
    </button>
  )
}

// ─── ReportIssue ────────────────────────────────────────────────────

export function ReportIssueRow({
  onTap,
  strings = RouteDetailsStrings,
}: {
  onTap: () => void
  strings?: RouteDetailsStringSet
}): ReactElement {
  return (
    <button
      type="button"
      className={styles.reportIssue}
      data-testid={RouteDetailsTestTags.reportIssue}
      onClick={onTap}
    >
      <span className={styles.reportIcon}>
        <ReportIcon />
      </span>
      <span className={styles.reportLabel}>{strings.reportIssue}</span>
    </button>
  )
}

function ReportIcon(): ReactElement {
  // Speech-bubble with "!" — matches the Figma RD/Row-ReportIssue glyph
  // (a rounded chat bubble). `currentColor`, tinted text-secondary.
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9A1.5 1.5 0 0 1 18.5 16H9l-4 3.5V16H5.5A1.5 1.5 0 0 1 4 14.5v-9Z"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      <path d="M12 7.5v4" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" />
      <circle cx="12" cy="13.5" r="0.9" fill="currentColor" />
    </svg>
  )
}
